import React, { useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { isBrowser } from 'ws-scripts/modules/environment';
import { IncentivesContext, UIContext, FiltersContext } from '../contexts';
import { WIDGET_STATES } from '../utils/trackingConstants';
import { URL_FACET_KEYS } from '../hooks/useDeepLinking';
import { computeVehicleOfferCount } from '../utils/filterVehicles';
import selectVehicleCardImageUrl from '../utils/selectVehicleCardImageUrl';
import VehicleImage from './VehicleImage';
import VehicleTitle from './VehicleTitle';
import OfferCountBadge from './OfferCountBadge';

const VehicleCard = ({ vehicle, activeCategoryTrim }) => {
	const { vehicles } = useContext(IncentivesContext);
	const { changeView } = useContext(UIContext);
	const { activeFilters } = useContext(FiltersContext);
	const { widgetName, windowId } = useRequestData();
	const trackingFired = useRef(false);

	const vehicleData = vehicles?.[vehicle.globalVehicleId] || null;
	// Story 10.3 (F289003): per-category-render image precedence.
	// BR-COMP-002 step 1 (trim-scoped) → step 2 (base GV) → step 3 (NoImage).
	// BR-IMG-002: scoped per-render — no memoization across category boundaries.
	// BR-IMG-003: silent fallback to base media on miss.
	const imageUrl = selectVehicleCardImageUrl({
		imagesByTrim: vehicleData?.imagesByTrim,
		activeCategoryTrim,
		baseImageUrl: vehicleData?.vehicleMedia?.[0]?.url || null
	});
	// Trim ID tracking what's actually rendered: when a category trim is
	// active and we have its imagesByTrim entry, use that tid; otherwise fall
	// back to the vehicle-level default tid (the first successful trim seeded
	// during the SSR byTrim fan-out).
	const trimId =
		(activeCategoryTrim &&
			vehicleData?.imagesByTrim?.[activeCategoryTrim]
				?.globalVehicleTrimId) ||
		vehicleData?.globalVehicleTrimId ||
		null;
	const altText = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

	// Story 9.3 (F243497): cascade offer count with active offerTypes filter.
	// Only incentive-level filters (offerTypes) affect this count; vehicle-level
	// filters (year, bodyStyle, make, model) determine WHICH vehicles render
	// via filterVehicles upstream and are intentionally excluded here.
	const cascadingCount = useMemo(
		() => computeVehicleOfferCount(vehicle, activeFilters),
		[vehicle, activeFilters?.offerTypes]
	);

	const handleClick = () => {
		if (!trackingFired.current) {
			trackingFired.current = true;
			trackEvent(widgetName, windowId, {
				element: 'Vehicle card',
				action: 'clicked',
				result: 'Navigated to vehicle incentive details',
				widgetState: WIDGET_STATES.DEFAULT,
				priorityType: vehicle.priorityType,
				nonInteractive: false
			});
			setTimeout(() => {
				trackingFired.current = false;
			}, 300);
		}
		changeView('detail', vehicle.globalVehicleId);
		if (isBrowser) {
			// Story 9.2 (F243497): preserve URL_FACET_KEYS (currently
			// ['offerTypes']) across listing→detail navigation so deep-link
			// offer-type context survives and DetailsView can pre-select the
			// matching tab. Drop every other facet/query param per BR-NAV
			// (keep detail URLs clean), then set gvId.
			const url = new URL(window.location);
			const keys = [...url.searchParams.keys()];
			keys.forEach((key) => {
				if (!URL_FACET_KEYS.includes(key)) {
					url.searchParams.delete(key);
				}
			});
			url.searchParams.set('gvId', vehicle.globalVehicleId);
			window.history.pushState(null, '', url);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleClick();
		}
	};

	const dataLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model} Incentive Details`;

	return (
		<div
			className="vehicle-card box box-border"
			data-gv-id={vehicle.globalVehicleId}
			data-trim-id={trimId}
			data-label={dataLabel}
			role="button"
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			aria-label={altText}
		>
			<VehicleImage imageUrl={imageUrl} alt="" />
			<div className="vehicle-card__body">
				<VehicleTitle vehicle={vehicle} />
				<span className="vehicle-card__subtitle">
					{vehicle.year} &bull; {vehicle.bodyStyle}
				</span>
			</div>
			<div className="vehicle-card__footer">
				<OfferCountBadge offerCount={cascadingCount} />
			</div>
		</div>
	);
};

VehicleCard.propTypes = {
	vehicle: PropTypes.shape({
		globalVehicleId: PropTypes.string.isRequired,
		year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
			.isRequired,
		make: PropTypes.string.isRequired,
		model: PropTypes.string.isRequired,
		bodyStyle: PropTypes.string,
		offerTypes: PropTypes.arrayOf(PropTypes.string),
		offerCount: PropTypes.number.isRequired,
		priorityType: PropTypes.string
	}).isRequired,
	// F289003 — set by GroupedVehicleGrid per-category-render slot. When
	// present and a matching imagesByTrim entry exists on the SSR vehicle data,
	// the trim-scoped jellybean is rendered. Omitted in non-showcase contexts.
	activeCategoryTrim: PropTypes.string
};

VehicleCard.defaultProps = {
	activeCategoryTrim: null
};

export default VehicleCard;
